import { MemoryStick } from "lucide-react";
import type { ReactNode } from "react";

import type { MemoryAggregate, Scope } from "@busright/data-contract";

import { Drillable } from "@/components/Drillable";
import { MetricLabel } from "@/components/MetricTooltip";
import { formatMb, formatPct } from "@/lib/format";

// =================================================================================================
// FhMemoryFact — reframes the unexplained "memory 66" score into a FACT: used/total/free MB, plus
// the derived used% ratio. The 0–100 memory SUBSCORE still lives in FhSubscoreBreakdown (it feeds
// the composite and stays explainable via its own tooltip); this panel is the concrete number an
// operator can act on. Every figure is Drillable (opens the per-device memory dataset) and carries
// a MetricLabel tooltip explaining exactly what it means and how it's computed.
// =================================================================================================

export interface FhMemoryFactProps {
  readonly fleet: MemoryAggregate | null;
  readonly scope: Scope;
  readonly loading: boolean;
}

export const FhMemoryFact = ({ fleet, scope, loading }: FhMemoryFactProps): ReactNode => {
  if (loading) {
    return <div className="fh-memory-fact-skeleton" />;
  }
  if (!fleet || fleet.avgUsedMb === null || fleet.avgTotalMb === null) {
    return <p className="card-subtitle">No memory readings yet.</p>;
  }
  return (
    <Drillable metricKey="memory.usedMb" scope={scope} label="Drill into per-device memory usage">
      <div className="fh-memory-fact">
        <div className="fh-memory-fact-head">
          <MemoryStick size={16} aria-hidden />
          <MetricLabel metricKey="memory.usedMb" label="Memory used (fact, not a score)" />
        </div>
        <div className="metric-strip">
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.usedMb" label="Used" />
            </span>
            <span className="mc-value br-tabular-nums">{formatMb(fleet.avgUsedMb)}</span>
          </div>
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.totalMb" label="Total" />
            </span>
            <span className="mc-value br-tabular-nums">
              {fleet.avgTotalMb === null ? "—" : formatMb(fleet.avgTotalMb)}
            </span>
          </div>
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.freeMb" label="Free" />
            </span>
            <span className="mc-value br-tabular-nums">
              {fleet.avgFreeMb === null ? "—" : formatMb(fleet.avgFreeMb)}
            </span>
          </div>
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.usedPct" label="Used %" />
            </span>
            <span className="mc-value br-tabular-nums">
              {fleet.avgUsedPct === null ? "—" : formatPct(fleet.avgUsedPct)}
            </span>
          </div>
        </div>
      </div>
    </Drillable>
  );
};
